import { useCallback, useRef, useState } from "react";
import type { CreativeSubject } from "@digital-twins/shared-types";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { BrandSubjectsList } from "@/types/creative-library";

/** The new model a create step is filling in, and the one create call that saves it. */
export function useModelDraft(brandToken: string, list: BrandSubjectsList) {
  const [chosen, setChosen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<CreativeSubject["kind"]>("person");
  const lastCreateFailed = useRef(false);

  const clear = useCallback(() => {
    setChosen(false);
    setName("");
    setKind("person");
    lastCreateFailed.current = false;
  }, []);

  /** A response can be lost after the server saved, so a retry looks before it creates again. */
  async function findSavedByLastCreate(trimmedName: string) {
    if (!lastCreateFailed.current) return undefined;
    const known = list.subjects.map((subject) => subject.token);
    const current = await creativeLibraryApi.subjects(brandToken);
    return current.find(
      (subject) =>
        !known.includes(subject.token) &&
        subject.name === trimmedName &&
        subject.kind === kind,
    );
  }

  /** Saves the draft, adds it to the list and clears the fields. */
  async function create() {
    const trimmedName = name.trim();
    try {
      const created =
        (await findSavedByLastCreate(trimmedName)) ??
        (await creativeLibraryApi.createSubject({
          name: trimmedName,
          kind,
          guidance: "",
          brandToken,
          referenceTokens: [],
          primaryReferenceToken: null,
          referenceViews: {},
        }));
      list.add(created);
      clear();
      return created;
    } catch (error) {
      lastCreateFailed.current = true;
      throw error;
    }
  }

  return {
    chosen,
    name,
    kind,
    setName,
    setKind,
    choose: () => setChosen(true),
    clear,
    create,
  };
}
